import { autoinject, transient, BindingEngine, computedFrom, observable, Disposable, useView } from "aurelia-framework";
import { ChildComponent } from "../../child-component";
import {
    SafetyRatingReviewClientModel, AddSubtaskRequest, SafetyRatingReviewOutcomeStepEnum,
    SafetyRatingReviewStatusEnum, SafetyRatingReviewTaskClientModel
} from "../../../../../models/vetting/vetting.models";
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { UserSecurityInfoModel } from "../../../../../models/authentication/token.models";
import { Assignment } from "../../../../components/assignment/assignment";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { DynamicColumnModel, EntityDynamicValue } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { DataType } from "../../../../../models/queries/queries.models";
import { Reflection } from "../../../../../utilities/reflection";
import * as Enumerable from "linq";
import { Messages } from "../../../../../configuration/event-messages.constants";
import { SafetyRatingReviewService } from "../../../../../services/vetting/safety-rating-review.service";
import { SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";

@autoinject
@transient()
@useView('views/pages/modules/safety-score-review/tabs/safety-score-review-review-details.html')
export class SafetyScoreReviewReviewDetails extends ChildComponent<SafetyRatingReviewClientModel> {
    currentUserId: number;
    subtaskStatuses: LookupValue[];
    canAddSafetyRatingReviewRfi: boolean;

    changeStatusToAwaitingResponse: boolean = true;
    assignToMe: any;
    viewCorrespondence: any;
    isRightShipUser: boolean;
    canAssignSafetyRatingReview: boolean;
    currentUser: UserSecurityInfoModel;
    passed: number = SafetyRatingReviewOutcomeStepEnum.Pass;
    failed: number = SafetyRatingReviewOutcomeStepEnum.Fail;
    ruleDetailsEl: Element;
    assignment: Assignment;
    reloadItemFunc: ((safetyRatingReviewTaskId: number) => void);
    filter: string;

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item.safetyRatingReviewStatusId))
    get isClosed() {
        return this.item && (this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted2
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AcceptedFromSystem
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Canceled
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Rejected);
    }

    constructor(i18N: I18N,
        private signalClientService: SignalClientService,
        private userClientService: UserClientService,
        private safetyRatingReviewService: SafetyRatingReviewService,
        private cacheClient: CacheClientService,
        private userClaimsService: UserClaimsService,
        private notificationClientService: NotificationClientService,
        private dialogClientService: DialogClientService,
        private eventAggregator: EventAggregator,
        lookupClientService: LookupClientService, protected bindingEngine: BindingEngine
    ) {
        super(notificationClientService, i18N);
        this.currentUser = userClientService.currentUser;
        this.canAssignSafetyRatingReview = this.userClaimsService.hasPermission(Claims.CanAssignSafetyRatingReview);
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAddSafetyRatingReviewRfi = this.userClaimsService.hasPermission(Claims.CanAddSafetyRatingReviewRfi);

        const requestSubtaskStatuses = new LookupValueRequest();
        requestSubtaskStatuses.lookupValueQuery = LookupValueQuery.SubtaskStatuses;
        lookupClientService.getLookup(requestSubtaskStatuses).then(x => {
            this.subtaskStatuses = x.activeValues;
        });

        this.currentUserId = this.userClientService.currentUser.id;
    }

    currentTask: SafetyRatingReviewTaskClientModel;
    displayName(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.displayName;
    }

    isTextBox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Default ||
            column.tableColumnDataTypeId == DataType.Number ||
            column.tableColumnDataTypeId == DataType.Double;
    }

    getTextBoxType(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        if (column.tableColumnDataTypeId == DataType.Default) {
            return "text";
        }
        if (column.tableColumnDataTypeId == DataType.Number) {
            return "integer";
        }
        if (column.tableColumnDataTypeId == DataType.Double) {
            return "double";
        }
    }

    isCheckbox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Boolean;
    }

    isDate(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.DateTime;
    }

    async viewTask(task: SafetyRatingReviewTaskClientModel) {
        this.currentTask = task;
        if (!task) return;
        if (this.currentTask.hasListItems && !this.currentTask.listItems) {
            this.currentTask.listItems = await this.safetyRatingReviewService.getListItems(this.currentTask.safetyRatingReviewTaskId);
        }

        setTimeout(() => {
            if (this.currentTask && this.currentTask.element) {
                this.currentTask.element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }, 100);
    }

    clearFilter() {
        this.filter = null;
    }

    getCount(outcomeId: number) {
        return this.item && this.item.safetyRatingReviewTasks ? this.item.safetyRatingReviewTasks
            .filter(x => x.vetSuperSafetyRatingReviewTaskOutcomeId == outcomeId || (outcomeId == null && x.vetSuperSafetyRatingReviewTaskOutcomeId == 3)).length : 0;
    }

    filterClick(outcomeId: number) {
        const index = this.selectedSafetyRatingReviewTaskStatusIds.indexOf(outcomeId);
        if (index >= 0)
            this.selectedSafetyRatingReviewTaskStatusIds.splice(index, 1);
        else
            this.selectedSafetyRatingReviewTaskStatusIds.push(outcomeId);
    }

    saveComments() {
        return new Promise<void>((resolve, reject) => {
            this.safetyRatingReviewService.saveComments(this.item.safetyRatingReviewId, [{
                safetyRatingReviewTaskId: this.currentTask.safetyRatingReviewTaskId,
                vettingComment: this.currentTask.vettingComment
            }], this.item.otherComments).then(() => {
                this.currentTask.safetyRatingReviewCommentDateModified = new Date();
                this.notificationClientService.show("Comment", "Comment saved successfully", NotificationType.Success);
                resolve();
            }, () => {
                this.notificationClientService.show("Comment", "Comment failed to save", NotificationType.Error);
                reject();
            });
        });
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item.safetyRatingReviewTasks),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item.safetyRatingReviewTasks.length))
    get anyNotReviewed() {
        if (!this.item || !this.item.safetyRatingReviewTasks)
            return false;

        const tasks = this.item.safetyRatingReviewTasks.filter(x => !x.isReviewed).length;
        return tasks > 0;
    }

    activate(model: { item: SafetyRatingReviewClientModel, assignment: Assignment }) {
        super.activate(model.item);

        this.assignment = model.assignment;
    }

    protected onActivated(model: SafetyRatingReviewClientModel) {
        super.onActivated(model);

        if (this.item) {
            this.loadRFIs();
        }

        const completedStatuses = [
            SafetyRatingReviewStatusEnum.Accepted2,
            SafetyRatingReviewStatusEnum.AcceptedFromSystem,
            SafetyRatingReviewStatusEnum.Canceled,
            SafetyRatingReviewStatusEnum.Rejected
        ];

        if (completedStatuses.indexOf(model.safetyRatingReviewStatusId) > -1) {
            this.selectedSafetyRatingReviewTaskStatusIds = [];
        }
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(v => v.item),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(v => v.item.safetyRatingReviewStatusId))
    public get statusIsFinal(): boolean {
        return this.item && (this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Accepted2
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.AcceptedFromSystem
            || this.item.safetyRatingReviewStatusId == SafetyRatingReviewStatusEnum.Canceled);
    }

    async updateTaskOutcome(outcome: number, task: SafetyRatingReviewTaskClientModel = null) {
        if (this.isClosed || (task.safetyRatingReviewTaskOutcomeId == 2 && !task.canOverrideComputedFailedOutcome)) return;

        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            const safetyRatingReviewTaskId = task ? task.safetyRatingReviewTaskId : this.currentTask.safetyRatingReviewTaskId;
            const vetSuperSafetyRatingReviewTaskOutcomeId = task ? task.vetSuperSafetyRatingReviewTaskOutcomeId : this.currentTask.vetSuperSafetyRatingReviewTaskOutcomeId;
            const newOutcome = vetSuperSafetyRatingReviewTaskOutcomeId == outcome ? 3 : outcome;
            await this.safetyRatingReviewService.updateTaskOutcome(safetyRatingReviewTaskId, newOutcome);
            this.currentTask.computedOutcome = false;
            this.currentTask.vetSuperSafetyRatingReviewTaskOutcomeId = newOutcome;
            this.currentTask.vetSuperLastModifiedDate = new Date();
            this.currentTask.vetSuperLastModifiedName = this.userClientService.currentUser.userName;
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.SafetyScoreReviewAssignmentDialog, {
                name: "Review",
                submit: this.assignToMe.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }


    outcomes: LookupValue[] = [
        { name: "Review Required", id: null },
        { name: "Passed", id: 1 },
        { name: "Failed", id: 2 }
    ];

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item.safetyRatingReviewTasks.length),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.filter),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.selectedSafetyRatingReviewTaskStatusIds),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.selectedSafetyRatingReviewTaskStatusIds.length))
    get safetyRatingReviewTasks(): { [index: number]: SafetyRatingReviewTaskClientModel[] } {
        const tasks: { [index: number]: SafetyRatingReviewTaskClientModel[] } = {};

        let safetyRatingReviewTasks = this.item.safetyRatingReviewTasks;
        if (this.selectedSafetyRatingReviewTaskStatusIds && this.selectedSafetyRatingReviewTaskStatusIds.length > 0)
            safetyRatingReviewTasks = safetyRatingReviewTasks.filter(x => this.selectedSafetyRatingReviewTaskStatusIds.indexOf(x.vetSuperSafetyRatingReviewTaskOutcomeId) > -1);

        if (this.filter)
            safetyRatingReviewTasks = safetyRatingReviewTasks.filter(x => (x.ruleCode && x.ruleCode.toLowerCase().indexOf(this.filter.toLowerCase()) > -1) || (x.title && x.title.toLowerCase().indexOf(this.filter.toLowerCase()) > -1));

        for (const task of safetyRatingReviewTasks) {
            const categoryId = task.categoryId;
            if (tasks[categoryId]) {
                tasks[categoryId].push(task);
            } else {
                tasks[categoryId] = [task];
            }
        }
        return tasks;
    }

    applyOutcomeFilters(x: any) {
        this.selectedSafetyRatingReviewTaskStatusIds = x.filters.map(x => x.id);
        this.showSafetyRatingReviewTaskStatusFilter = false;
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.safetyRatingReviewTasks),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item.safetyRatingReviewTasks.length))
    get safetyRatingReviewTaskKeys() {
        return Object.keys(this.safetyRatingReviewTasks);
    }

    getTitle(taskGroup: number) {
        if (taskGroup == 1)
            return "Safety Score 1/5 rules";
        if (taskGroup == 2)
            return "Safety Score 2/5 rules";

        return "";
    }

    closeRuleDetails() {
        this.currentTask = null;
    }

    toggle(element: HTMLElement, arrow: HTMLElement) {
        $(element).slideToggle()
        if ($(arrow).hasClass('is-opened')) {
            $(arrow).removeClass('is-opened');
        } else {
            $(arrow).addClass('is-opened');
        }
    }

    addSubtask() {
        this.addRFIs()
    }

    countRFIs: number = 0;
    rfis: { [index: number]: AddSubtaskRequest } = {};

    addRFIs() {
        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                entityId: this.item.safetyRatingReviewId,
                entityTypeId: this.item.entityType,
                requestedBy: this.item.requestedBy,
                emails: this.item.initialEmailsForCorrespondence,
                createSubtask: true,
                isPartialRFI: true,
                reload: (() => {
                    this.loadRFIs();
                    this.safetyRatingReviewService.getSafetyRatingReviewTasks(this.item.safetyRatingReviewId).then(x => {
                        this.item.safetyRatingReviewTasks = x;
                    });
                }).bind(this)
            },
                true, false, false, 'right');
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.SafetyScoreReviewAssignmentDialog, {
                name: "Review",
                submit: this.assignToMe.bind(this),
                callback: this.addRFIs.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }

    }

    rfi(task: SafetyRatingReviewTaskClientModel) {
        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                entityId: this.item.safetyRatingReviewId,
                entityTypeId: this.item.entityType,
                emails: this.item.initialEmailsForCorrespondence,
                linkedEntityId: task.safetyRatingReviewTaskId,
                linkedEntityTypeId: EntityTypes.SafetyRatingReviewTask,
                isPartialRFI: true,
                subtaskTypes: [],
                reload: (() => {
                    this.loadRFIs();
                    this.safetyRatingReviewService.getSafetyRatingReviewTasks(this.item.safetyRatingReviewId).then(x => {
                        this.item.safetyRatingReviewTasks = x;
                    });
                }).bind(this)
            },
                true, false, false, 'right');
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.SafetyScoreReviewAssignmentDialog, {
                name: "Request",
                submit: this.assignToMe.bind(this),
                callback: this.rfi.bind(this, task),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    private loadRFIs() {
        this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(`RFI-SRR-${this.item.safetyRatingReviewId}`, CacheLocation.SessionStorage, false).then(x => {
            if (x) {
                this.countRFIs = Enumerable.from(x).where(x => x.value != null).count();
                this.rfis = x;
            } else {
                this.countRFIs = 0;
                this.rfis = {};
            }
        });
    }

    async rerunRules() {
        try {
            await this.safetyRatingReviewService.rerunRules(this.item.vesselId, this.item.safetyRatingReviewId);
            this.notificationClientService.show("Re-run Rules", "Successfully placed in queue", NotificationType.Success);
        } catch (e) {
            this.notificationClientService.show("Re-run Rules", "Error", NotificationType.Error);
            throw e;
        }
    }

    @observable()
    public showSafetyRatingReviewTaskStatusFilter: boolean = false;
    public selectedSafetyRatingReviewTaskStatusIds: number[] = [];

    public toggleSafetyRatingReviewTaskStatusFilter($event) {
        if ($event)
            $event.stopPropagation();
        if (!this.showSafetyRatingReviewTaskStatusFilter) this.closeAllDropdowns();
        this.showSafetyRatingReviewTaskStatusFilter = !this.showSafetyRatingReviewTaskStatusFilter;
    }

    closeAllDropdowns() {
        this.showSafetyRatingReviewTaskStatusFilter = false;
    }

    _subscriptionToDocumentClickEvent: Disposable;
    async attached() {
        this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeAllDropdowns.bind(this));
    }

    detached() {
        this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    private refreshRating = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.item.vesselId).length >= 1) {
            try {
                if (this.reloadItemFunc)
                    await this.reloadItemFunc(this.currentTask ? this.currentTask.safetyRatingReviewTaskId : null)
            } catch (e) {
                console.error("Failed to refresh rating", e);
            }
        }
    }


    @computedFrom("selectedSafetyRatingReviewTaskStatusIds", "selectedSafetyRatingReviewTaskStatusIds.length")
    public get safetyRatingReviewTaskStatusFilterActive(): boolean {
        return this.selectedSafetyRatingReviewTaskStatusIds && this.selectedSafetyRatingReviewTaskStatusIds.length > 0;
    }

    public clearSafetyRatingReviewTaskStatusFilter() {
        this.selectedSafetyRatingReviewTaskStatusIds = [];
    }

    get pendingRules() {
        return this.item.safetyRatingReviewTasks.filter(task => !task.vetSuperSafetyRatingReviewTaskOutcomeId || task.vetSuperSafetyRatingReviewTaskOutcomeId <= 0).length;
    }

    @computedFrom("item", "item.initialEmailsForCorrespondence", "item.initialEmailsForCorrespondence.length")
    get shipManagerEmails() {
        if (this.item.initialEmailsForCorrespondence)
            return this.item.initialEmailsForCorrespondence.filter(x => x.isShipManagerEmail);
        return [];
    }

    rulesContent: HTMLElement;

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.rulesContent),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.item.safetyRatingReviewTasks.length),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.rulesContent.offsetTop),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.currentTask),
        Reflection.getPropertyPath<SafetyScoreReviewReviewDetails>(z => z.currentTask.safetyRatingReviewTaskId))
    get paddingTop() {
        // PX-6635
        if (Enumerable.from(this.safetyRatingReviewTasks).sum(x => x.value.length) > 4) {
            const task = Enumerable.from(this.safetyRatingReviewTasks).selectMany<SafetyRatingReviewTaskClientModel>(x => x.value)
                .firstOrDefault(x => this.currentTask && this.currentTask.safetyRatingReviewTaskId == x.safetyRatingReviewTaskId);
            if (task && task.element && this.rulesContent)
                return task.element.offsetTop - this.rulesContent.offsetTop;
        }

        return 0;
    }

}